import { useId, type KeyboardEvent, type ReactNode } from 'react';
import './Tabs.css';

export interface TabOption<T extends string = string> {
  value: T;
  label: string;
  /** Optional trailing count/badge — the `2` in "Shortcuts 2". Decorative: it is `aria-hidden`, so
   * the tab's name stays the word alone. Pass the same fact into the panel if AT needs it. */
  hint?: string;
}

export interface TabsProps<T extends string = string> {
  value: T;
  onChange: (value: T) => void;
  options: TabOption<T>[];
  /** The tablist's accessible name — what these tabs are tabs OF ("Settings sections"). */
  label: string;
  /** The panel for `value`. One panel is rendered at a time; the caller decides what that costs. */
  children: ReactNode;
}

/**
 * DESIGN.md §9.1 `Tabs`. Named in the inventory since the first pass and built in MVP-396, which is
 * the first screen with enough sections to need one.
 *
 * Deliberately NOT `SegmentedControl` wearing different CSS. That is a `role="radiogroup"` — an
 * exclusive CHOICE, a value the operator is setting — and these are tabs, a place the operator is
 * standing. A screen reader announces the two differently ("radio, 2 of 5" vs "tab, 2 of 5,
 * selected"), and only one of them tells Sam that the rest of the screen just changed underneath.
 *
 * Roving tabindex per the WAI-ARIA tabs pattern: one tab in the page tab order, arrows move between
 * them, Home/End jump to the ends. Activation follows selection (moving the arrow key switches the
 * panel) because every panel here is local state with nothing to fetch — the "manual activation"
 * variant exists for panels that are expensive to open, which these are not.
 */
export function Tabs<T extends string = string>({ value, onChange, options, label, children }: TabsProps<T>) {
  const baseId = useId();
  const tabId = (v: T) => `${baseId}-tab-${v}`;
  const panelId = `${baseId}-panel`;

  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    const index = options.findIndex((o) => o.value === value);
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = (index + 1) % options.length;
    else if (e.key === 'ArrowLeft') next = (index - 1 + options.length) % options.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = options.length - 1;
    if (next === null) return;
    e.preventDefault();
    const target = options[next];
    onChange(target.value);
    // The new tab has to take focus as well as selection, or the arrow key moves the panel out from
    // under a focus ring that stays on the old tab — the roving part of "roving tabindex".
    document.getElementById(tabId(target.value))?.focus();
  }

  return (
    <div className="tabs">
      <div className="tabs__list" role="tablist" aria-label={label}>
        {options.map((opt) => {
          const selected = opt.value === value;
          return (
            <button
              key={opt.value}
              id={tabId(opt.value)}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={selected ? panelId : undefined}
              tabIndex={selected ? 0 : -1}
              className={`tabs__tab${selected ? ' tabs__tab--active' : ''}`}
              onClick={() => onChange(opt.value)}
              onKeyDown={onKeyDown}
            >
              {opt.label}
              {opt.hint && (
                <span className="tabs__hint text-meta tabular-nums" aria-hidden="true">
                  {opt.hint}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {/* `tabIndex={0}`: the panel has no focusable child in some states (a section that is all
          static text), and the ARIA tabs pattern asks that such a panel be reachable by Tab so the
          keyboard operator can scroll it. */}
      <div id={panelId} role="tabpanel" aria-labelledby={tabId(value)} tabIndex={0} className="tabs__panel">
        {children}
      </div>
    </div>
  );
}
